import { AccommodationLayout } from '@components/dashboard/accommodation/AccommodationLayout';
import { Sidebar } from '@components/dashboard/superuser/Sidebar';
import { ACCOMMODATION_SIDEBAR_SECTIONS } from '@components/dashboard/superuser/sidebarConfig';
import { useAppSelector } from '@hooks/useRedux';
import { AccessControlCode } from '@shared/constants';
import {
  selectAccommodationIsLoading,
  selectAccommodationProperty,
} from '@store/accommodation';
import {
  selectCurrentUser,
  selectHasAccessControl,
} from '@store/auth/authSelectors';
import { Building2, Hotel } from 'lucide-react';
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { OutletSelector } from '../components/common';
import { UserActionsMenu } from '../components/nav/UserActionsMenu';

export const AccommodationPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const canAccess = useAppSelector(
    selectHasAccessControl(AccessControlCode.ACCOMMODATION_PAGE_ACCESS),
  );
  const property = useAppSelector(selectAccommodationProperty);
  const isLoading = useAppSelector(selectAccommodationIsLoading);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (!canAccess) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Hotel size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-600">Access restricted</p>
          <p className="mt-1 text-xs text-gray-400">
            You do not have permission to access accommodation.
          </p>
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="mt-4 text-xs text-pos-primary underline"
          >
            Back to sales
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans text-sm text-gray-900">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
        sections={ACCOMMODATION_SIDEBAR_SECTIONS}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Hotel size={32} className="text-pos-primary" />
            <div className="flex-1">
              <h1 className="text-base font-semibold text-gray-900">
                Accommodation
              </h1>
              {property && (
                <p className="text-xs text-gray-400">{property.name}</p>
              )}
            </div>
            {isLoading && !property && (
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <Building2 size={13} />
                Loading property…
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <OutletSelector />
            <UserActionsMenu />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-7xl">
            {/* <div className="flex h-96 flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-gray-200 bg-white"> */}
            <AccommodationLayout />
            {/* </div> */}
          </div>
          {/* No-property onboarding */}
          {/* {!isLoading && !property ? (
              <div className="flex h-96 flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-gray-200 bg-white">
                <Building2 size={40} className="text-gray-300" />
                <div className="text-center">
                  <p className="text-base font-semibold text-gray-700">
                    Set up your property
                  </p>
                  <p className="mt-1 text-sm text-gray-400">
                    Configure your accommodation property to start managing
                    bookings.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/accommodation/settings/properties')}
                  className="flex items-center gap-2 rounded-xl bg-pos-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-pos-primary/90"
                >
                  <Building2 size={14} />
                  Create your property
                </button>
              </div>
            ) : (
              <AccommodationLayout />
            )}
          </div> */}
        </main>
      </div>
    </div>
  );
};

export default AccommodationPage;
